import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router';
import { useMultiTabLogout } from '../../../../../common/hooks/useMultiTabLogout';
import Icons from '../../../../../common/widgets/Icons';
import LoadingSpinner from '../../../../../common/widgets/LoadingSpinner';
import { MenuListItem } from '../../../../../common/widgets/MenuListItem';
import { CustomerInfoContext } from '../../../contexts/CustomerInfoContext';
import { CustomerPermissionsContext } from '../../../contexts/CustomerPermissionsContext';
import { resources } from '../../../resources/resources';

interface IAdminMenuProps {
  readonly menuClose?: () => void;
}

export default function AdminMenu(props: IAdminMenuProps) {
  const { menuClose } = props;
  const { t: translate } = useTranslation();
  const { triggerLogout } = useMultiTabLogout();

  const navigate = useNavigate();
  const auth = useAuth();
  const permissions = useContext(CustomerPermissionsContext);
  const { customerInfo } = useContext(CustomerInfoContext);

  const handleClick = (linkPath: string) => {
    return () => {
      navigate(linkPath);
      if (menuClose) menuClose();
    };
  };

  const handleLogout = async () => {
    triggerLogout();
    await auth.signoutRedirect();
  };

  if (permissions.isLoading) {
    return (
      <div className="action-panel">
        <div className="action-panel-content account-nav">
          <LoadingSpinner size="medium" />
        </div>
      </div>
    );
  }

  return (
    <div className="action-panel">
      <div className="action-panel-content account-nav">
        <ul>
          {permissions.canManageTimesheets &&
            customerInfo.canAccessTimeAndAttendanceModule && (
              <MenuListItem
                text={translate(resources.menu.attendance)}
                icon={<Icons.CalendarEdit />}
                customOnClick={handleClick('/admin/attendance')}
                linkPath="/admin/attendance"
              />
            )}
          {permissions.canManageTimesheets &&
            customerInfo.canAccessTimeAndAttendanceModule && (
              <MenuListItem
                text={translate(resources.admin.timesheetsSettings.types.title)}
                customOnClick={handleClick(
                  '/admin/attendance/timesheet-settings',
                )}
                icon={<></>}
                linkPath="/admin/attendance/timesheet-settings"
              />
            )}
          {permissions.canManageEmployees && (
            <MenuListItem
              text={translate(resources.menu.employeeManagement)}
              icon={<Icons.Employees />}
              customOnClick={handleClick('/admin/employee-management')}
              linkPath="/admin/employee-management"
            />
          )}
          {permissions.canImportCards && (
            <MenuListItem
              text={translate(resources.menu.importCards)}
              icon={<Icons.DownloadFile />}
              customOnClick={handleClick('/admin/cards-import')}
              linkPath="/admin/cards-import"
            />
          )}
          {permissions.canImportOrders && (
            <MenuListItem
              text={translate(resources.menu.importOrders)}
              icon={<Icons.DownloadFile />}
              customOnClick={handleClick('/admin/orders-import')}
              linkPath="/admin/orders-import"
            />
          )}
          {permissions.canImportTimesheets && (
            <MenuListItem
              text={translate(resources.menu.importTimesheets)}
              icon={<Icons.DownloadFile />}
              customOnClick={handleClick('/admin/timesheets-import')}
              linkPath="/admin/timesheets-import"
            />
          )}
          <MenuListItem
            text={translate(resources.menu.employeeBenefits)}
            icon={<Icons.ShoppingCartCheck />}
            customOnClick={handleClick('/admin/employee-benefits')}
            linkPath="/admin/employee-benefits"
          />
          {permissions.canManageCompanies && (
            <MenuListItem
              text={translate(resources.menu.companySettings)}
              icon={<Icons.Cog />}
              customOnClick={handleClick('/admin/companies-settings')}
              linkPath="/admin/companies-settings"
            />
          )}
          {permissions.canGenerateReports && (
            <MenuListItem
              text={translate(resources.menu.finalReports)}
              icon={<Icons.Report />}
              customOnClick={handleClick('/admin/final-reports')}
              linkPath="/admin/final-reports"
            />
          )}
          <MenuListItem
            text={translate(resources.menu.generalStatistics)}
            icon={<Icons.Statistic />}
            customOnClick={handleClick('/admin/general-statistics')}
            linkPath="/admin/general-statistics"
          />
          <MenuListItem
            text={translate(resources.menu.employeeFiles)}
            icon={<Icons.DownloadComputer />}
            customOnClick={handleClick('/admin/employee-files')}
            linkPath="/admin/employee-files"
          />
          <MenuListItem
            text={translate(resources.menu.logout)}
            icon={<Icons.Logout />}
            customOnClick={handleLogout}
          />
        </ul>
      </div>
    </div>
  );
}
